// Typography presets — colours come from the UI palette, so a change there
// flows to every screen that uses a preset instead of drifting apart from it.
//
// ─── Every size below is a step from the source ──────────────────────────────
// design-source/suricata-design-language.html defines the scale, DESIGN.md §6
// tabulates it, and src/constants/tokens.js exports it verbatim as TYPE_SCALE.
// This file had quietly built a PARALLEL scale in whole numbers — 13 where the
// source says 13.5, 12 where it says 12.5, 14 where it says 14.5, 20 where it
// says 18 — and because ~790 call sites take their sizes from these presets or
// copy their values, the half-point scale the design was drawn on never
// reached the screen. The mapping is now explicit, and named per ROLE rather
// than per step, because the source's own names do not run in size order:
//
//   --fs-h1  30   page title            --fs-body     16     body
//   --fs-h3  21   lead headline         --fs-body-sm  14.5   secondary body
//   --fs-h2  18   inner heading         --fs-ui       13.5   controls, labels
//   --fs-h3-sm 16.5 large label         --fs-ui-sm    12.5   kickers
//   --fs-metric 28  KPI number          --fs-meta     11.5   timestamps
//                                       --fs-num-*    13.5 · 15.5 · 11
import { UI, FONT } from "./tokens";

export const TYPE = {
  // Tight labels. --fs-ui / --fs-h3-sm.
  label:       { fontSize: 13.5, fontWeight: 500, color: UI.text.secondary, fontFamily: FONT.body },
  labelLarge:  { fontSize: 16.5, fontWeight: 500, color: UI.text.secondary, fontFamily: FONT.body },

  // Numeric values — Archivo, tabular figures.
  // --fs-h2 · --fs-h3 · --fs-metric.
  value:       { fontSize: 18, fontWeight: 600, color: UI.text.primary, fontFamily: FONT.latin, fontVariantNumeric: "tabular-nums" },
  valueLarge:  { fontSize: 21, fontWeight: 700, color: UI.text.primary, fontFamily: FONT.latin, fontVariantNumeric: "tabular-nums" },
  valueXL:     { fontSize: 28, fontWeight: 800, color: UI.text.primary, fontFamily: FONT.latin, fontVariantNumeric: "tabular-nums" },

  // Headings — Heebo (the display voice). Canonical scale:
  // Letter spacing is "normal" on every Hebrew tier (sprint 17): tracking,
  // tight or loose, pulls Hebrew letters out of their word shapes.
  // h1 (one per screen) > h2 (card/section titles) > heading (inner blocks)
  // > headingSub (uppercase kickers).
  h1:          { fontSize: 30,   fontWeight: 800, color: UI.text.primary,   fontFamily: FONT.heading, letterSpacing: "normal", lineHeight: 1.25 },
  h2:          { fontSize: 21,   fontWeight: 800, color: UI.text.primary,   fontFamily: FONT.heading, letterSpacing: "normal", lineHeight: 1.5 },
  heading:     { fontSize: 18,   fontWeight: 700, color: UI.text.primary,   fontFamily: FONT.heading, letterSpacing: "normal", lineHeight: 1 },
  headingSub:  { fontSize: 12.5, fontWeight: 700, color: UI.text.secondary, fontFamily: FONT.heading,
                 textTransform: "uppercase", letterSpacing: "normal" },

  // Caption — smallest legible tier (timestamps, sources, footnotes). --fs-meta.
  caption:     { fontSize: 11.5, fontWeight: 500, color: UI.text.muted, fontFamily: FONT.body, lineHeight: 1.5 },

  // Body — Assistant (the UI voice). Line heights are the source's too.
  body:        { fontSize: 16,   lineHeight: 1.7,  color: UI.text.primary,   fontFamily: FONT.body },
  bodySmall:   { fontSize: 14.5, lineHeight: 1.75, color: UI.text.secondary, fontFamily: FONT.body },

  // Hero price. --fs-metric: a KPI number, the role this actually plays.
  // (The 52/74 hero steps belong to the portfolio value alone.)
  price:       { fontSize: 28, fontWeight: 800, color: UI.text.primary, fontFamily: FONT.latin, fontVariantNumeric: "tabular-nums" },
};

// ─── The heaviest weight the product loads is 800 ────────────────────────────
// public/fonts.css ships Heebo 400/500/700/800, Assistant 400/600/700 and
// Archivo 500/600 (and 400, for the shekel line only, as "Archivo Shekel"). Thirty call sites asked for 900, which no face provides —
// the browser either synthesised it or clamped to the nearest real weight, so
// the "heaviest" headings in the app were not the weight they claimed.
// Loading a 900 face was rejected: the design language's heaviest step is 800,
// and adding a face would spend bytes on weight the source never asks for.
export const MAX_WEIGHT = 800;
